import { ChangeEvent, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import dayjs from "dayjs";

import { translate } from "@ascendnow/shared";

import { PausePlayer, PlayPlayer } from "../AscIcons";
import styles from "./AscAudioPlayer.module.scss";

type audioStatus = "inactive" | "playing" | "paused";

export type AscAudioPlayerProps = {
  src: string;
  label?: string;
  className?: string;
  onPlay?: () => void;
  onPause?: () => void;
};

export default function AscAudioPlayer({
  src,
  label,
  className,
  onPlay,
  onPause,
}: AscAudioPlayerProps) {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [audioStatus, setAudioStatus] = useState<audioStatus>("inactive");
  const [percentagePosition, setPercentagePosition] = useState(0);

  const handlePlay = () => {
    audioRef.current?.play();
    setAudioStatus("playing");
  };

  const handlePause = () => {
    audioRef.current?.pause();
    setAudioStatus("paused");
  };

  const handlePlayController = () => {
    if (audioRef.current) {
      if (audioStatus === "playing") {
        handlePause();
        onPause?.();
      } else {
        handlePlay();
        onPlay?.();
        setPercentagePosition(1);
      }
    }
  };

  const handleAudioEnd = () => {
    onPause?.();
    setAudioStatus("inactive");
    setPercentagePosition(0);
  };

  const changeTimelinePosition = () => {
    if (audioRef.current) {
      const percentagePosition =
        (100 * audioRef.current?.currentTime) / audioRef.current?.duration;
      setPercentagePosition(percentagePosition);
    }
  };

  const handleChangeSeek = (e: ChangeEvent<HTMLInputElement>) => {
    if (audioRef.current) {
      const currentTimelineValue = Number(e.target.value);
      const time = (currentTimelineValue * audioRef.current.duration) / 100;
      if (audioRef.current.currentTime) {
        audioRef.current.currentTime = time || 0;
      }
    }
  };

  const loadedMetaData = () => {
    if (audioRef.current && audioRef.current.duration === Infinity) {
      audioRef.current.currentTime = 1e101;
      audioRef.current.ontimeupdate = function (this: GlobalEventHandlers) {
        this.ontimeupdate = () => {
          return;
        };
        if (audioRef.current && audioRef.current.currentTime) {
          audioRef.current.currentTime = 0;
        }
      };
    }
  };

  useEffect(() => {
    if (audioRef.current && src && !src.includes('blob')) {
      audioRef.current.load();
    }
  }, [src]);

  return (
    <div
      data-testid="asc-audio-player"
      className={classNames(styles.ground, className)}
    >
      {label && <span className={styles.label}>{label}</span>}
      <div className={styles.audioWrapper}>
        <button
          type="button"
          className={styles.playController}
          onClick={handlePlayController}
        >
          {audioStatus === "playing" ? <PausePlayer /> : <PlayPlayer />}
        </button>
        {src && (
          <>
            <audio
              ref={audioRef}
              onEnded={handleAudioEnd}
              onTimeUpdate={changeTimelinePosition}
              onLoadedMetadata={loadedMetaData}
              preload="auto"
            >
              {translate("audioNotSupport")}
              <source src={src} type="audio/mpeg" />
              <source src={src} type="audio/ogg" />
              <source src={src} type="audio/webm" />
              <source src={src} type="video/webm" />
              <source src={src} type="audio/mp3" />
              <source src={src} type="audio/wav" />
            </audio>
            <div className={styles.timelineWrapper}>
              <span className={styles.currentTime}>
                {dayjs
                  .duration(audioRef.current?.currentTime || 0, "seconds")
                  .format("mm:ss")}
              </span>
              <input
                type="range"
                className={styles.timeline}
                max="100"
                value={percentagePosition?.toFixed(0)}
                style={{ backgroundSize: `${percentagePosition}% 100%` }}
                onChange={handleChangeSeek}
              />
              <span className={styles.duration}>
                {dayjs
                  .duration(audioRef?.current?.duration || 0, "seconds")
                  .format("mm:ss")}
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
